import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Play, LogOut, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MenuMusic } from "@/components/MenuMusic";
import { SupporterBadge } from "@/components/SupporterBadge";
import { FilmSignup } from "@/components/FilmSignup";
import { ChapterArt } from "@/components/ChapterArt";

const ChapterCard = ({ c, onBuy, onPlay, busy, idx }) => (
  <div className={`panel p-6 lg:p-10 flex flex-col rise ${idx === 0 ? "md:row-span-2" : ""}`} style={{ animationDelay: `${idx * 120}ms` }} data-testid={`chapter-card-${c.number}`}>
    <p className="pixel-text text-lg text-[#7DD3FC] tracking-widest">CHAPTER {c.number}</p>
    <h2 className="font-display text-2xl lg:text-4xl text-[#F3F4F6] mt-1">{c.title}</h2>
    <ChapterArt chapter={c.number} />
    <p className="pixel-text text-xl lg:text-2xl text-[#9CA3AF] mt-3 flex-1">{c.blurb}</p>
    <div className="mt-6 flex items-center justify-between gap-3">
      {c.owned ? <span className="pixel-text text-lg text-[#F59E0B]" data-testid={`chapter-${c.number}-owned`}>{c.available ? "OWNED" : ""}</span>
        : <span className="pixel-text text-2xl text-[#F3F4F6]">${c.price.toFixed(2)}</span>}
      {!c.available && c.preorder && !c.owned ? (
        <button className="pixel-btn" disabled={busy} onClick={() => onBuy(c)} data-testid={`chapter-${c.number}-preorder-button`}>{busy ? "..." : `Pre-order $${c.price.toFixed(2)}`}</button>
      ) : !c.available && c.owned ? (
        <span className="pixel-text text-lg text-[#F59E0B] flex items-center gap-2" data-testid={`chapter-${c.number}-preordered`}><Lock size={14} /> PRE-ORDERED · COMING SOON</span>
      ) : !c.available ? (
        <span className="pixel-text text-lg text-[#64748B] flex items-center gap-2" data-testid={`chapter-${c.number}-coming-soon`}><Lock size={14} /> COMING SOON</span>
      ) : c.owned ? (
        <button className="pixel-btn flex items-center gap-2" onClick={() => onPlay(c)} data-testid={`chapter-${c.number}-play-button`}><Play size={14} /> Play</button>
      ) : (
        <button className="pixel-btn" disabled={busy} onClick={() => onBuy(c)} data-testid={`chapter-${c.number}-buy-button`}>{busy ? "..." : `Unlock $${c.price.toFixed(2)}`}</button>
      )}
    </div>
  </div>
);

const DeleteSaves = () => {
  const wipe = async () => {
    try { const { data } = await api.delete("/progress"); toast.success(`All saves deleted (${data.deleted})`); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button className="pixel-btn ghost flex items-center gap-2" data-testid="delete-all-saves-button"><Trash2 size={14} /><span className="hidden sm:inline">Delete Saves</span></button>
      </AlertDialogTrigger>
      <AlertDialogContent className="rounded-none bg-[#12141C] border-2 border-[#2E3440] shadow-[6px_6px_0_#000]" data-testid="delete-saves-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-2xl text-[#F3F4F6]">Delete all saves?</AlertDialogTitle>
          <AlertDialogDescription className="pixel-text text-xl text-[#9CA3AF]">
            Every chapter's progress, items and journal will be erased. Your purchased chapters stay unlocked.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="pixel-btn ghost rounded-none h-auto" data-testid="delete-saves-cancel-button">Cancel</AlertDialogCancel>
          <AlertDialogAction className="pixel-btn rounded-none h-auto" onClick={wipe} data-testid="delete-saves-confirm-button">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const FlickerWord = () => {
  const [god, setGod] = useState(true);
  useEffect(() => { const id = setInterval(() => setGod((g) => !g), 1900); return () => clearInterval(id); }, []);
  return (
    <span className="inline-grid text-[#DC2626] align-baseline">
      <span className="invisible col-start-1 row-start-1" aria-hidden="true">Nightmare</span>
      <span key={god ? "god" : "nightmare"} className="col-start-1 row-start-1 word-flicker" data-testid="menu-flicker-word">{god ? "God" : "Nightmare"}</span>
    </span>
  );
};

export default function ChaptersPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [chapters, setChapters] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/chapters").then((r) => setChapters(r.data)).catch((e) => toast.error(apiError(e))); }, []);

  const buy = async (c) => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { chapter_id: c.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  return (
    <div className="min-h-screen px-4 sm:px-8 lg:px-12 py-8 w-full max-w-[1800px] mx-auto flex flex-col pb-20" data-testid="chapters-page">
      <MenuMusic />
      <header className="flex items-center justify-between gap-4 mb-10">
        <span className="font-display text-lg sm:text-2xl font-black whitespace-nowrap">GODS WE CALL</span>
        <div className="flex items-center gap-3 sm:gap-5">
          <DeleteSaves />
          <span className="pixel-text text-xl text-[#9CA3AF] hidden sm:inline" data-testid="current-username">@{user?.username}</span>
          {user?.username?.toLowerCase() === "disabler7" && <button className="pixel-btn ghost" onClick={() => nav("/admin")} data-testid="admin-link-button">Admin</button>}
          <SupporterBadge hint />
          <button className="pixel-btn ghost flex items-center gap-2" onClick={logout} data-testid="logout-button"><LogOut size={14} /><span className="hidden sm:inline">Exit</span></button>
        </div>
      </header>
      <h1 className="font-blocky text-base sm:text-3xl lg:text-5xl whitespace-nowrap leading-relaxed rise" data-testid="menu-headline">Awaken your <FlickerWord /></h1>
      <p className="pixel-text text-xl text-[#9CA3AF] mt-4 mb-12 max-w-xl">Each chapter is $0.99. Your progress is saved to your account.</p>
      <div className="grid md:grid-cols-[1.4fr_1fr] gap-6 flex-1 md:min-h-[60vh]" data-testid="chapter-list">
        {chapters.map((c, i) => <ChapterCard key={c.id} c={c} idx={i} busy={busy} onBuy={buy} onPlay={(ch) => nav(`/play/${ch.id}`)} />)}
      </div>
      <section className="mt-8 border-2 border-dashed border-[#7f1d1d] bg-[#7f1d1d]/10 p-5 sm:p-6 grid md:grid-cols-[1.4fr_1fr] gap-4 md:gap-8 items-center" data-testid="menu-film-signup">
        <div>
          <p className="font-blocky text-[9px] sm:text-[10px] text-[#DC2626] tracking-widest mb-2">▶ A FEATURE FILM</p>
          <p className="pixel-text text-xl text-[#F3F4F6] leading-snug">Gods We Call will be made into a feature film. Leave your email to hear news first: casting, trailers and release dates.</p>
        </div>
        <FilmSignup source="menu" />
      </section>
    </div>
  );
}
